import { useState } from "react";
import { Users, RefreshCw, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTeam, TeamMember } from "@/hooks/useTeam";
import { useAuth } from "@/hooks/useAuth";
import { TeamMemberCard } from "@/components/team/TeamMemberCard";
import { TeamMemberEditDialog } from "@/components/team/TeamMemberEditDialog";
import { AddTeamMemberDialog } from "@/components/team/AddTeamMemberDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function EquipePage() {
  const { members, isLoading, fetchTeam, removeMember, getRoleLabel, getRoleOptions } = useTeam();
  const { user } = useAuth();
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removingMember, setRemovingMember] = useState<TeamMember | null>(null);

  const handleRemoveMember = async () => {
    if (!removingMember) return;
    
    await removeMember(removingMember.id, removingMember.profile?.full_name || "");
    setRemovingMember(null);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Equipe</h1>
            <p className="text-sm text-muted-foreground">
              Gerencie os membros da sua equipe
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchTeam()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <Button size="sm" onClick={() => setAddDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Adicionar
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            Nenhum membro encontrado
          </h3>
          <p className="text-muted-foreground mb-4">
            Adicione funcionários à sua equipe
          </p>
          <Button onClick={() => setAddDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Adicionar Funcionário
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {members.map((member) => (
            <TeamMemberCard
              key={member.id}
              member={member}
              getRoleLabel={getRoleLabel}
              onEdit={() => setEditingMember(member)}
              onRemove={() => setRemovingMember(member)}
              canRemove={member.user_id !== user?.id && member.role !== "restaurant_owner"}
            />
          ))}
        </div>
      )}

      <TeamMemberEditDialog
        member={editingMember}
        open={!!editingMember}
        onOpenChange={(open) => !open && setEditingMember(null)}
        getRoleOptions={getRoleOptions}
        onSuccess={() => {
          setEditingMember(null);
          fetchTeam();
        }}
      />

      <AddTeamMemberDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={() => fetchTeam()}
      />

      <AlertDialog open={!!removingMember} onOpenChange={(open) => !open && setRemovingMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover funcionário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover{" "}
              <strong>{removingMember?.profile?.full_name || "este membro"}</strong>{" "}
              da equipe? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
